import React, { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { recruitmentApi } from "../../services/recruitmentApi";
import { formatApiErrorDetail } from "../../lib/api";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { ReasonDialog } from "../../components/ReasonDialog";
import { usePermissions } from "../../hooks/usePermissions";
import { StoreSelect } from "../../components/StoreSelect";
import { useLocalization } from "../../hooks/useLocalization";

const EMPTY_FORM = { job_posting_id: "", interviewer_name: "", date: "", start_time: "10:00", end_time: "10:30", store_location: "" };

export default function InterviewSlotsPage() {
  const { t } = useLocalization();
  const { canManageRecruitment } = usePermissions();
  const [slots, setSlots] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [cancellingSlotId, setCancellingSlotId] = useState(null);

  const load = () => recruitmentApi.listInterviewSlots().then((r) => setSlots(r.data)).catch(() => {});

  useEffect(() => {
    if (!canManageRecruitment) return;
    load();
    recruitmentApi.listJobs().then((r) => setJobs(r.data)).catch(() => {});
  }, [canManageRecruitment]);

  if (!canManageRecruitment) return <p className="text-sm text-gray-500" data-testid="interview-slots-access-denied">{t("common.accessDenied")}</p>;

  const jobTitle = (id) => jobs.find((j) => j.id === id)?.title_en;
  const cancellingSlot = slots.find((s) => s.id === cancellingSlotId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await recruitmentApi.createInterviewSlot({ ...form, job_posting_id: form.job_posting_id || null });
      toast.success(t("mgmtRecruitment.slotCreated"));
      setForm(EMPTY_FORM);
      setShowForm(false);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleCancel = async (reason) => {
    await recruitmentApi.cancelInterviewSlot(cancellingSlotId, reason);
    toast.success(t("mgmtRecruitment.slotCancelled"));
    load();
  };

  return (
    <div className="space-y-4" data-testid="interview-slots-page">
      <PageHelpBanner pageKey="interview-slots" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtRecruitment.slotsHelp") }} />
      </PageHelpBanner>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtRecruitment.slotsTitle")}</h2>
          <p className="text-sm text-gray-500">{t("mgmtRecruitment.slotsSubtitle")}</p>
        </div>
        <button data-testid="new-interview-slot-button" onClick={() => setShowForm((s) => !s)} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">
          <Plus size={15} /> {t("mgmtRecruitment.newInterviewSlot")}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-5 space-y-3" data-testid="interview-slot-form">
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="slot-job-select" value={form.job_posting_id} onChange={(e) => setForm((f) => ({ ...f, job_posting_id: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm col-span-2">
              <option value="">{t("mgmtRecruitment.anyOpenPosition")}</option>
              {jobs.map((j) => <option key={j.id} value={j.id}>{j.title_en}</option>)}
            </select>
            <input data-testid="slot-interviewer-input" required placeholder={t("mgmtRecruitment.interviewerNamePlaceholder")} value={form.interviewer_name} onChange={(e) => setForm((f) => ({ ...f, interviewer_name: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
            <StoreSelect testId="slot-store-location-input" value={form.store_location} onChange={(v) => setForm((f) => ({ ...f, store_location: v }))} allowEmpty emptyLabel={t("mgmtScheduling.everyStoreLabel")} className="px-3 py-2" />
            <input data-testid="slot-date-input" required type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
            <input data-testid="slot-start-time-input" required type="time" value={form.start_time} onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
            <input data-testid="slot-end-time-input" required type="time" value={form.end_time} onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
          </div>
          {error && <p className="text-sm text-red-500" data-testid="interview-slot-form-error">{error}</p>}
          <button data-testid="submit-interview-slot-button" type="submit" className="px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">{t("mgmtRecruitment.createSlot")}</button>
        </form>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t("common.date")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtRecruitment.colTime")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtRecruitment.position")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtRecruitment.colInterviewer")}</th>
              <th className="px-4 py-3 font-medium">{t("common.status")}</th>
              <th className="px-4 py-3 font-medium">{t("common.cancel")}</th>
            </tr>
          </thead>
          <tbody>
            {slots.map((slot) => (
              <tr key={slot.id} className="border-t border-gray-100" data-testid={`interview-slot-row-${slot.id}`}>
                <td className="px-4 py-3">{slot.date}</td>
                <td className="px-4 py-3 text-xs">{slot.start_time} - {slot.end_time}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{jobTitle(slot.job_posting_id) || t("mgmtRecruitment.anyPosition")}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{slot.interviewer_name}</td>
                <td className="px-4 py-3"><StatusBadge status={slot.status} /></td>
                <td className="px-4 py-3">
                  {slot.status !== "CANCELLED" && (
                    <button data-testid={`cancel-interview-slot-${slot.id}`} onClick={() => setCancellingSlotId(slot.id)} className="text-red-500 hover:text-red-700"><X size={14} /></button>
                  )}
                </td>
              </tr>
            ))}
            {slots.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">{t("mgmtRecruitment.noSlots")}</td></tr>}
          </tbody>
        </table>
      </div>

      {cancellingSlot && (
        <ReasonDialog
          open onOpenChange={(v) => !v && setCancellingSlotId(null)} title={t("mgmtRecruitment.cancelInterviewSlotTitle")} danger
          contextItems={[
            { label: t("common.date"), value: `${cancellingSlot.date} ${cancellingSlot.start_time}-${cancellingSlot.end_time}` },
            { label: t("mgmtRecruitment.colInterviewer"), value: cancellingSlot.interviewer_name },
            { label: t("mgmtRecruitment.position"), value: jobTitle(cancellingSlot.job_posting_id) || t("mgmtRecruitment.anyPosition") },
          ]}
          reasonLabel={t("mgmtRecruitment.cancelReasonLabel")} confirmLabel={t("mgmtRecruitment.cancelSlot")}
          onConfirm={handleCancel}
          testId="cancel-interview-slot-dialog"
        />
      )}
    </div>
  );
}
